import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useCareProfiles } from '../hooks/useCareProfiles';

// ─────────────────────────────────────────────────────────────────────────────
// DietaryFilterContext
//
// Lifts dietary filter state to a shared context so that DietaryFilterPanel
// (which toggles profiles) and ProductsView (which calls filterProducts)
// are always reading from and writing to the same state.
//
// Previously both called useDietaryFilter() independently, creating two
// separate useState instances that never communicated.
// ─────────────────────────────────────────────────────────────────────────────

const DietaryFilterContext = createContext(null);

export const DietaryFilterProvider = ({ children }) => {
  const { careProfiles = [], loading } = useCareProfiles();
  const [selectedProfileIds, setSelectedProfileIds] = useState([]);

  const isFilterActive = selectedProfileIds.length > 0;

  // ── Selected profiles ─────────────────────────────────────────────────────

  const getActiveProfiles = useCallback(() => {
    if (selectedProfileIds.length === 0) return [];
    return careProfiles.filter(p => selectedProfileIds.includes(p.id));
  }, [careProfiles, selectedProfileIds]);

  // ── Aggregate allergens — reads iOS field: profile.allergies ──────────────

  const getCombinedAllergens = useCallback(() => {
    const set = new Set();
    getActiveProfiles().forEach(profile => {
      (profile.allergies || []).forEach(a => set.add(a.toLowerCase().trim()));
    });
    return Array.from(set);
  }, [getActiveProfiles]);

  // ── Aggregate avoids — reads iOS field: profile.dietaryRestrictions ───────

  const getCombinedAvoids = useCallback(() => {
    const set = new Set();
    getActiveProfiles().forEach(profile => {
      (profile.dietaryRestrictions || []).forEach(r => set.add(r.toLowerCase().trim()));
    });
    return Array.from(set);
  }, [getActiveProfiles]);

  // ── Dietary tags (display only) — reads iOS field: profile.tags ──────────

  const getCombinedDietaryTags = useCallback(() => {
    const set = new Set();
    getActiveProfiles().forEach(profile => {
      (profile.tags || []).forEach(t => {
        const raw = typeof t === 'string' ? t : (t?.dietaryTag || t?.rawValue || '');
        if (raw) set.add(raw);
      });
    });
    return Array.from(set);
  }, [getActiveProfiles]);

  // ── Toggle / select / clear ───────────────────────────────────────────────

  const toggleProfile = useCallback((profileId) => {
    setSelectedProfileIds(prev =>
      prev.includes(profileId)
        ? prev.filter(id => id !== profileId)
        : [...prev, profileId]
    );
  }, []);

  const selectAllProfiles = useCallback(() => {
    // Select only non-admin display profiles
    const displayProfiles = careProfiles.filter(p => !p.isAdmin);
    setSelectedProfileIds(displayProfiles.map(p => p.id));
  }, [careProfiles]);

  const clearAllProfiles = useCallback(() => {
    setSelectedProfileIds([]);
  }, []);

  // ── Core compatibility check — mirrors iOS DietaryFilterService ───────────

  const isProductCompatible = useCallback((product, allergens, avoids) => {
    const normalize = s => (s || '').toLowerCase().trim();

    const ingredientNames = Array.isArray(product.ingredients)
      ? product.ingredients.map(i =>
          typeof i === 'string' ? normalize(i) : normalize(i?.name)
        )
      : [];

    const ingredientsText  = normalize(product.ingredientsText || '');
    const productAllergens = (product.allergens || []).map(normalize);

    const containsTerm = (term) => {
      const t = normalize(term);
      return (
        ingredientNames.some(n => n.includes(t)) ||
        ingredientsText.includes(t) ||
        productAllergens.some(a => a.includes(t))
      );
    };

    if (allergens.some(a => containsTerm(a))) return false;
    if (avoids.some(a => containsTerm(a)))    return false;
    return true;
  }, []);

  // ── filterProducts ────────────────────────────────────────────────────────

  const filterProducts = useCallback((products) => {
    if (!isFilterActive || !Array.isArray(products)) return products;

    const allergens = getCombinedAllergens();
    const avoids    = getCombinedAvoids();

    if (allergens.length === 0 && avoids.length === 0) return products;

    return products.filter(p => isProductCompatible(p, allergens, avoids));
  }, [isFilterActive, getCombinedAllergens, getCombinedAvoids, isProductCompatible]);

  // ── filterRecipes ─────────────────────────────────────────────────────────

  const filterRecipes = useCallback((recipes) => {
    if (!isFilterActive || !Array.isArray(recipes)) return recipes;

    const allergens = getCombinedAllergens();
    const avoids    = getCombinedAvoids();

    if (allergens.length === 0 && avoids.length === 0) return recipes;

    const normalize = s => (s || '').toLowerCase().trim();

    return recipes.filter(recipe => {
      const ingredientNames = Array.isArray(recipe.ingredients)
        ? recipe.ingredients.map(i =>
            typeof i === 'string' ? normalize(i) : normalize(i?.name)
          )
        : [];

      const containsTerm = (term) => {
        const t = normalize(term);
        return ingredientNames.some(n => n.includes(t));
      };

      if (allergens.some(a => containsTerm(a))) return false;
      if (avoids.some(a => containsTerm(a)))    return false;
      return true;
    });
  }, [isFilterActive, getCombinedAllergens, getCombinedAvoids]);

  const value = useMemo(() => ({
    careProfiles,
    selectedProfileIds,
    isFilterActive,
    loading,
    toggleProfile,
    selectAllProfiles,
    clearAllProfiles,
    filterProducts,
    filterRecipes,
    getCombinedAllergens,
    getCombinedAvoids,
    getCombinedDietaryTags,
    getActiveProfiles,
  }), [
    careProfiles, selectedProfileIds, isFilterActive, loading,
    toggleProfile, selectAllProfiles, clearAllProfiles,
    filterProducts, filterRecipes,
    getCombinedAllergens, getCombinedAvoids, getCombinedDietaryTags, getActiveProfiles,
  ]);

  return (
    <DietaryFilterContext.Provider value={value}>
      {children}
    </DietaryFilterContext.Provider>
  );
};

// ── useDietaryFilter — reads from shared context ──────────────────────────

export const useDietaryFilter = () => {
  const ctx = useContext(DietaryFilterContext);
  if (!ctx) throw new Error('useDietaryFilter must be used inside DietaryFilterProvider');
  return ctx;
};
